// state.js — the single mutable state object the dashboard uses
// to track loaded data + UI selections.
//
// Everything in this object is read by multiple modules; everything
// is written by either the polling tick (bootstrap.js
// renderAll → fetchAll) or by per-page filter handlers.
//
// This is the live binding — `import { state } from "./state.js"`
// in any module returns the same object. Mutations (`state.leases
// = ...`) are visible everywhere on the next read.
//
// Field categories:
//
//   - Live data from the daemon/coordinator:
//       info, leases, leasesAll, files, events, activeAgents,
//       heartbeats
//
//   - Connection / loading state:
//       connected, loading, token
//
//   - Per-page UI selections (filters, pagination, expansion):
//       activePage, expandedChats, chatFilterText, chatPage,
//       eventTypeFilter, eventFilterText, eventPage,
//       leaseFilterText, leaseFilterState, leaseFilterRepo
//
//   - Cached derived data:
//       deviceLabels
//
//   - Crypto / team identity:
//       hmacKey, hmacKeyB64
//
//   - Misc:
//       allowRevoke (settings flag), members (team), teamInfo
//
// New fields: prefer adding to a per-page module's local state if
// only that page reads/writes them. Add here only when at least
// two modules need to share the value.
export const state = {
  info: null,
  leases: [],
  leasesAll: [],
  events: [],
  // eventsLoaded: an events fetch has completed. Events arrive after the
  // first paint (after `loading` clears), so the Events lists need their own
  // flag to tell "still loading" from "no events".
  eventsLoaded: false,
  connected: false,
  loading: true,
  activePage: "home",
  expandedChats: new Set(),
  eventTypeFilter: "all",
  // eventSubTypeFilter narrows the Events list to a SINGLE exact event type
  // (e.g. "branch.contended") within the active category. "all" = no
  // sub-filter. Driven by the types actually present in state.events so it
  // never offers an empty option. Reset to "all" whenever the category chip
  // changes (a new category has a different set of types).
  eventSubTypeFilter: "all",
  // eventQuery: set when a number on another page opened the Events page
  // with its filter (event-links.js); the list then comes from the server.
  eventQuery: null,
  eventQueryPage: 0,
  chatFilterText: "",
  chatPage: 0,
  // overviewView: "mine" | "team". The Overview home scope toggle
  // (team admins, hosted only). "mine" scopes the tiles + timeline to
  // the signed-in user; "team" shows the whole team and relabels the
  // timeline by member + repo. Mirrors the URL ?view= param; widgets.js
  // reads this to branch its labels without re-parsing the URL.
  overviewView: "mine",
  // chatScopeView: "mine" | "team". The Chats page scope toggle (hosted
  // only, all members). Default "team" preserves the page's existing
  // whole-team view; "mine" is an opt-in narrowing to the signed-in
  // user's own chats. Session-only.
  chatScopeView: "team",
  // chatMemberFilter: when a Chats-page member chip is selected (team
  // view), the chat list narrows to that member's display name. "" = all.
  chatMemberFilter: "",
  leaseFilterText: "",
  leaseFilterState: "held",
  // leaseFilterRepo: empty string = all repos. Otherwise the exact
  // repo string (as computed by repoFor()) that a row's repo
  // column must match. Populated from the chip row at the top of
  // the leases page.
  leaseFilterRepo: "",
  // leaseFilterMineOnly: v0.38.0 Phase 4b. When true, fetchLeasesOnce
  // adds ?actor_subject=<whoami.identity> to /v1/teams/leases so the
  // table narrows to leases the current OIDC-authed user triggered.
  // false means show every lease in the BU (legacy behavior). Toggle
  // lives on the Leases page top bar; persists for the session only
  // (deliberately not in localStorage — the toggle is meant for
  // focused-review moments, not a sticky preference).
  leaseFilterMineOnly: false,
  eventFilterText: "",
  eventPage: 0,
  activeAgents: [],
  allowRevoke: false,
  // null = not yet known (don't nudge); true/false once /v1/config is read.
  // Drives the "reads aren't tracked" hint on Browsing chats.
  trackReads: null,
  token: null,
  hmacKey: null,
  hmacKeyB64: null,
  localNetworkBlocked: false,
  // True while the Overview's cloud poll feeds state.leases (scoped to
  // My chats or Team); the team-wide refresh then leaves them alone.
  overviewOwnsLeases: false,
  // Lease ids an admin clicked Release on that the holder hasn't dropped yet.
  releasingLeases: new Set(),
  // Per-agent activity heartbeats from /v1/teams/heartbeats. Each
  // entry: { agent_id, laptop_id, last_file (hashed), last_tool,
  // display_hint (encrypted), device_hint (encrypted), last_seen,
  // age_seconds, lastFile (decrypted), lastTool (decrypted),
  // deviceLabel (decrypted) }
  //
  // BOTH the wire form and the decrypted form are kept because
  // decryption is async and aggregateChats is sync.
  heartbeats: [],
  // Device label map indexed by laptop_id. Populated from
  // heartbeat device_hint decrypts. Persists across renders/
  // refreshes so a stale chat without a fresh heartbeat in the
  // current cycle still resolves to its last-known label. Cleared
  // on signOut.
  deviceLabels: {},
};
